import { useState, type FormEvent } from 'react';
import { resetUserPassword } from '@/services/users';

interface ResetPasswordModalProps {
  userId: string;
  userName: string;
  userEmail: string;
  onClose: () => void;
}

export default function ResetPasswordModal({ userId, userName, userEmail, onClose }: ResetPasswordModalProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setSaving(true);
    try {
      await resetUserPassword(userId, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo restablecer la contraseña.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-md p-6 shadow-none border border-background-200">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-base font-semibold text-foreground-950">Cambiar contraseña</h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              {userName || 'Usuario'} · <span className="text-foreground-400">{userEmail}</span>
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
            <i className="ri-error-warning-line mt-0.5"></i>
            <span>{error}</span>
          </div>
        )}

        {done ? (
          <div className="space-y-4">
            <div className="flex items-start gap-2 text-sm bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-lg px-3 py-3">
              <i className="ri-checkbox-circle-line mt-0.5 text-emerald-600"></i>
              <div className="space-y-1">
                <p className="font-medium">Contraseña actualizada.</p>
                <p className="text-emerald-700">
                  Comparte la nueva contraseña con {userName || 'el usuario'} para que pueda iniciar sesión.
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
              >
                Listo
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-foreground-600 mb-1.5 block">Nueva contraseña</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground-600 mb-1.5 block">Confirmar nueva contraseña</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repite la contraseña"
                className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
              />
            </div>

            <div className="pt-2 flex gap-3 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="text-sm px-4 py-2.5 rounded-lg border border-background-200 text-foreground-700 hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
              >
                {saving ? 'Guardando...' : 'Guardar contraseña'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}